import React from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { brand, heroStats } from "../mock/content";
import { useConfigModal } from "./ConfigModal";
import { t } from "../lib/ui";

const HERO_IMG = "/img/ext/k5any45o_IMG_8500-3ed7d2.webp";

export default function Hero() {
  const { openConfigModal } = useConfigModal();
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative min-h-[86vh] md:min-h-screen w-full bg-black overflow-hidden">
      {/* Background: foto modulo — fascia a sinistra, entrata morbida da sinistra */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="group absolute inset-y-0 right-0 w-full md:w-[60%] overflow-hidden hero-slide-in">
          <div className="absolute inset-0 md:translate-y-[10%]">
            <img
              src={HERO_IMG}
              alt="Modulo fotovoltaico TORRI Half Cell con bordo X-CORE"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
              style={{ objectPosition: "20% 50%", filter: "saturate(1.55) brightness(1.06) contrast(1.04)" }}
            />
          </div>
          {/* Bagliore giallo che scorre una volta all'ingresso */}
          <div className="hero-glint" aria-hidden="true" />
          {/* Sfuma il bordo sinistro dell'immagine verso il nero (dove c'è il testo) */}
          <div className="hidden md:block absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-black to-transparent" />
        </div>
        {/* Mobile: scurisce in alto per la leggibilità dell'header */}
        <div className="md:hidden absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-black via-black/60 to-transparent" />
        {/* Sfumatura inferiore per la leggibilità di testi e statistiche */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 min-h-[86vh] md:min-h-screen flex flex-col justify-end pb-16 pt-32">
        <div className="mt-8 md:mt-2">
          <span className="inline-flex flex-col gap-2 text-white/60 uppercase text-[11px] tracking-[0.14em] sm:text-[13px] sm:tracking-[0.18em] mb-6" style={{ fontFamily: "Space Grotesk" }} aria-label={t("hero_eyebrow")}>
            <span className="whitespace-nowrap">{t("hero_eyebrow")}</span>
            <span className="flex h-[3px] w-full overflow-hidden rounded-[1px]" aria-hidden="true">
              <span className="w-1/3 h-full" style={{ background: "#009246" }} />
              <span className="w-1/3 h-full bg-white" />
              <span className="w-1/3 h-full" style={{ background: "#CE2B37" }} />
            </span>
          </span>
          <p className="max-w-2xl text-white/70 text-base md:text-lg leading-relaxed mb-10" style={{ fontFamily: "Inter" }}>
            {t("hero_body_1")}<span className="text-white font-medium">Made in Italy</span>{t("hero_body_2")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <button
              onClick={() => scrollTo("#tecnologia")}
              data-testid="hero-scopri-tecnologie-btn"
              className="hero-tech-pulse group inline-flex items-center justify-center gap-3 px-8 py-4 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform [@media(hover:hover)]:hover:scale-[1.03]"
              style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}
            >
              {t("cta_discover_tech")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              type="button"
              onClick={openConfigModal}
              data-testid="hero-configura-btn"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-white/30 text-white text-sm uppercase tracking-[0.14em] font-semibold hover:bg-white hover:text-black transition-colors"
              style={{ fontFamily: "Space Grotesk" }}
            >
              {t("cta_configure")}
            </button>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/15">
            {heroStats.map((s, i) => (
              <div key={s.label} className={`py-6 pr-6 ${i !== 0 ? "md:pl-6 md:border-l border-white/15" : ""}`}>
                <div className="text-white font-black text-3xl md:text-4xl" style={{ fontFamily: "Archivo" }}>
                  {s.value}
                </div>
                <div className="text-white/50 text-xs uppercase tracking-[0.12em] mt-1" style={{ fontFamily: "Space Grotesk" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 lg:right-10 z-10 text-white/60 animate-bounce hidden md:block">
        <ArrowDown size={22} />
      </div>
    </section>
  );
}
